import { useEffect, useState } from 'react'
import { Button, ButtonGroup, Form, InputGroup, Modal } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { useStore } from '../../../app/stores/store'
import { HammerIcon } from '../icons/AuctionIcons'
import ErrorMessage from '../ErrorMessage'
import ConfirmBidPopup from './ConfirmBidPopup'
import { currencyValue, formatCurrencyWithoutCents } from '../muiStyle/muiStyle'
import { BidMultiplier, CentMultiplier } from '../../../app/enums/prices'

function AuctionBidInput() {
  const { bidsStore, userStore } = useStore()
  const { auctionDetails } = bidsStore
  const [multiplier, setMultiplier] = useState<number>(0)
  const [bid, setBid] = useState(0)
  const [error, setError] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const { id } = useParams()
  const completed = new Date() > new Date(auctionDetails?.deadline as Date)
  const hasStarted = new Date() > new Date(auctionDetails?.startingDate as Date)
  const { t } = useTranslation()

  useEffect(() => {
    if (multiplier === 0 && auctionDetails) {
      setMultiplier(auctionDetails.minIncreasePrice * CentMultiplier)
    }
  }, [auctionDetails, multiplier])

  useEffect(() => {
    if (auctionDetails) {
      let calculatedBid = 0
      if (bidsStore.currentBid) {
        calculatedBid = bidsStore.currentBid / CentMultiplier + auctionDetails!.minIncreasePrice
      } else {
        calculatedBid = auctionDetails.minPrice / CentMultiplier + 1
      }
      setBid(Math.round(calculatedBid))
    }
  }, [bidsStore.currentBid, id, auctionDetails, userStore.user])

  if (completed) return <></>

  const increment = () => {
    let value = bid
    value += multiplier / CentMultiplier
    if (value < 100000000) {
      setBid(value)
    }
  }

  const decrement = () => {
    let value = bid
    value -= multiplier / CentMultiplier
    if (value > Math.round(bidsStore.currentBid / 100)) {
      setBid(value)
    }
  }

  const handleBidChange = (e: any) => {
    let { value } = e.target

    if (value === '' || value === null) {
      setBid(0)
    } else {
      value = value.replaceAll(',', '')
      const isValidInput = /^[0-9]+$/.test(value)
      const parsedValue = parseInt(value)

      if (isValidInput) {
        setBid(parsedValue)
      }
    }
  }

  const openConfrimBid = () => {
    let userSolvency
    if (auctionDetails?.attachment) {
      userSolvency = auctionDetails?.solvencyAttachment?.find(
        (solvency) => solvency.user!.id === userStore.user!.id,
      )
    }
    if (
      bid <= bidsStore.auctionDetails!.minPrice / 100 &&
      !bidsStore.auctionDetails?.allowBelowMin &&
      !bidsStore.currentBid
    ) {
      setError(t('err_higherMinPriceBid')!)
    } else if (bid <= bidsStore.currentBid / 100 || Number.isNaN(bid)) {
      setError(t('err_higherCurrentBid')!)
    } else if (bid < bidsStore.currentBid / 100 + auctionDetails!.minIncreasePrice) {
      setError(
        `${t('err_bid1')!} ${
          bidsStore.currentBid / 100 + auctionDetails!.minIncreasePrice
        } ${currencyName} ${t('err_bid2')!}`,
      )
    } else {
      setError('')
      if (!userStore.isLoggedIn) {
        toast.error(t('err_login'))
      } else if (!hasStarted) {
        toast.error(t('err_notStarted'))
      } else if (auctionDetails?.status === 2 || completed) {
        toast.error(t('err_ended'))
      } else if (userSolvency?.valid !== 2 && auctionDetails?.attachment) {
        toast.error(t('err_solvency'))
      } else if (bidsStore.latestBidder?.email === userStore.user?.email) {
        toast.error(t('err_highestBid'))
      } else {
        setShowConfirm(true)
      }
    }
  }

  const handleSubmit = () => {
    const obj = {
      bid,
      biddingId: auctionDetails?.id,
    }
    bidsStore.addBid(obj).then(() => {
      setShowConfirm(false)
    })
    setError('')
  }

  const multiplierBtn = [
    { value: (auctionDetails?.minIncreasePrice || 0) * BidMultiplier.Original * CentMultiplier },
    { value: (auctionDetails?.minIncreasePrice || 0) * BidMultiplier.Ten * CentMultiplier },
    { value: (auctionDetails?.minIncreasePrice || 0) * BidMultiplier.Hundred * CentMultiplier },
  ]

  const currencyName = currencyValue.find(
    (item) => item.value === bidsStore.auctionDetails?.post?.currency,
  )?.name

  return (
    <>
      <div className='d-flex flex-column mt-2' style={{ width: '100%' }}>
        <p className='opacity0-7'>
          {t('price_multiplier')} ({currencyName}):{' '}
        </p>
        <ButtonGroup size='lg' className='bid-multiplier'>
          {multiplierBtn.map(({ value }) => (
            <Button
              key={value}
              active={multiplier === value}
              onClick={() => setMultiplier(value)}
              variant='light'
            >
              {formatCurrencyWithoutCents(value)}
            </Button>
          ))}
        </ButtonGroup>
        <InputGroup size='lg' className='bid-multiplier-btn'>
          <Button type='button' variant='light' onClick={decrement}>
            -
          </Button>
          <InputGroup.Text className='input-bid-text'>{currencyName}</InputGroup.Text>
          <Form.Control
            type='text'
            placeholder={t('bid_now')!}
            value={bid === 0 ? '' : formatCurrencyWithoutCents(bid * 100)}
            onChange={handleBidChange}
            className='input-bid-value'
          />
          <Button type='button' variant='light' onClick={increment}>
            +
          </Button>
        </InputGroup>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ErrorMessage autoClose={5000} text={error} setError={() => setError('')} />
            </motion.div>
          )}
        </AnimatePresence>
        <button onClick={openConfrimBid} className='bid-now-btn'>
          <HammerIcon />
          {t('bid_now')}
        </button>
      </div>

      {/* Auction Cofirm Bid Modal */}
      <Modal size='lg' centered show={showConfirm} onHide={() => setShowConfirm(false)}>
        <ConfirmBidPopup
          handleClose={() => setShowConfirm(false)}
          handleSubmit={handleSubmit}
          loadingBtn={bidsStore.loadingButton}
          title={auctionDetails?.post?.title as string}
          price={bid}
        />
      </Modal>
    </>
  )
}
export default observer(AuctionBidInput)
